import type { BinBbox } from '../types';

export interface Size {
  width: number;
  height: number;
}

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface BinCropOpts {
  expansionFactor: number;
  minSidePx: number;
}

export const DEFAULT_EXPANSION_FACTOR = 4.0;
export const DEFAULT_MIN_SIDE_PX = 512;

// Symbols below this in *decode-coord* space are too small to trust:
// the decoder may have produced a low-confidence read, and our crop
// math has no signal worth amplifying. The dispatch path skips them.
export const MIN_QR_SIDE_PX = 10;

/**
 * Project a decoder-coord bbox into original-frame coords. Independent
 * x/y scaling — the decode raster preserves the source aspect ratio in
 * practice, but the math doesn't assume it.
 */
export function projectBBox(bbox: BinBbox, decodeSize: Size, originalSize: Size): BinBbox {
  const sx = originalSize.width / decodeSize.width;
  const sy = originalSize.height / decodeSize.height;
  return {
    x1: bbox.x1 * sx,
    y1: bbox.y1 * sy,
    x2: bbox.x2 * sx,
    y2: bbox.y2 * sy,
  };
}

/**
 * Square crop centered on the bbox center, clipped to the frame.
 * Clipping at a frame edge may yield a non-square rect — that's
 * acceptable; the LLM tolerates aspect drift on a single edge.
 *
 * If the bbox center projects past a frame edge (e.g. a stale bbox
 * after a camera-switch dimension flip), the result is a zero-area
 * rect rather than negative dimensions. Callers should treat
 * `w === 0 || h === 0` as a degenerate signal and skip dispatch.
 */
export function computeBinCrop(bbox: BinBbox, originalSize: Size, opts: BinCropOpts): Rect {
  const qrW = bbox.x2 - bbox.x1;
  const qrH = bbox.y2 - bbox.y1;
  const qrLong = Math.max(qrW, qrH);
  const sideRaw = Math.max(qrLong * opts.expansionFactor, opts.minSidePx);
  const maxLong = Math.max(originalSize.width, originalSize.height);
  const side = Math.min(sideRaw, maxLong);

  const cx = (bbox.x1 + bbox.x2) / 2;
  const cy = (bbox.y1 + bbox.y2) / 2;

  // Unclipped rect endpoints (right/bottom are exclusive).
  const sideR = Math.round(side);
  const xUnclipped = Math.round(cx - side / 2);
  const yUnclipped = Math.round(cy - side / 2);

  // Intersect with frame bounds. `Math.max(0, ...)` on the size
  // guards against the bbox center projecting outside the frame —
  // returns w=0/h=0 instead of negative dimensions (which would
  // propagate as silent 1-px crops via the encode path's
  // Math.max(1, rect.w) guard).
  const x = Math.max(0, xUnclipped);
  const y = Math.max(0, yUnclipped);
  const x2Clipped = Math.min(originalSize.width, xUnclipped + sideR);
  const y2Clipped = Math.min(originalSize.height, yUnclipped + sideR);

  return {
    x,
    y,
    w: Math.max(0, x2Clipped - x),
    h: Math.max(0, y2Clipped - y),
  };
}

/**
 * True for bboxes too small or malformed to crop reliably. Callers
 * skip dispatch for degenerate symbols rather than send a noisy crop
 * that the LLM has to discard anyway.
 */
export function isBBoxDegenerate(bbox: BinBbox): boolean {
  if (!Number.isFinite(bbox.x1) || !Number.isFinite(bbox.y1)) return true;
  if (!Number.isFinite(bbox.x2) || !Number.isFinite(bbox.y2)) return true;
  const w = bbox.x2 - bbox.x1;
  const h = bbox.y2 - bbox.y1;
  if (w < MIN_QR_SIDE_PX || h < MIN_QR_SIDE_PX) return true;
  return false;
}

export interface CenterTile {
  tile: ImageData;
  origin: { x: number; y: number };
}

/**
 * Extract the center 50%×50% of an ImageData as a new ImageData
 * (decoupled — caller can drop the source after). Used by the ROI
 * fallback in handleFrame: when full-frame decode at the downscaled
 * raster returns zero hits for N consecutive frames, this lets us
 * run a second decode at *native* resolution on the operator's
 * likely target region (camera typically centered on the bin).
 */
export function extractCenterTile(source: ImageData): CenterTile {
  const tileW = Math.floor(source.width / 2);
  const tileH = Math.floor(source.height / 2);
  const originX = Math.floor((source.width - tileW) / 2);
  const originY = Math.floor((source.height - tileH) / 2);
  const tile = new ImageData(tileW, tileH);
  const srcStride = source.width * 4;
  const tileStride = tileW * 4;
  for (let y = 0; y < tileH; y++) {
    const srcStart = (originY + y) * srcStride + originX * 4;
    tile.data.set(source.data.subarray(srcStart, srcStart + tileStride), y * tileStride);
  }
  return { tile, origin: { x: originX, y: originY } };
}

/**
 * Bboxes returned by decoding a center tile are in tile-local coords.
 * Shift back into the source ImageData's coordinate space so they can
 * flow through the rest of the dispatch pipeline (projectBBox /
 * computeBinCrop) as if they had come from a full-frame decode.
 */
export function projectTileBBox(bbox: BinBbox, tileOrigin: { x: number; y: number }): BinBbox {
  return {
    x1: bbox.x1 + tileOrigin.x,
    y1: bbox.y1 + tileOrigin.y,
    x2: bbox.x2 + tileOrigin.x,
    y2: bbox.y2 + tileOrigin.y,
  };
}
